import React, { useState, useEffect } from 'react';
import { Image } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLightbulb, FaClipboardList, FaUserCircle, FaShieldAlt, FaChartLine, FaSun, FaMoon, FaBinoculars, FaSearch, FaRobot, FaBolt, FaFileAlt } from 'react-icons/fa';
import AdminManager from '../services/AdminManager';
import { useTheme } from '../theme/ThemeContext';
import OrgSwitcher from './OrgSwitcher';
import i18n from '../i18n';

const WebNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const isRTL = i18n.language === 'he';

  useEffect(() => {
    const checkAdmin = async () => {
      const adminStatus = await AdminManager.checkAdminStatus();
      setIsAdmin(adminStatus);
    };
    checkAdmin();
  }, []);

  const isActive = (path) => {
    if (path === '/insights') return location.pathname === '/' || location.pathname === '/insights';
    if (path === '/chatbot') return location.pathname === '/chatbot';
    if (path === '/signals') return location.pathname.startsWith('/signals');
    if (path === '/tracking') return location.pathname === '/tracking' || location.pathname.startsWith('/tracking/');
    if (path === '/reports') return location.pathname.startsWith('/reports');
    if (path === '/search') return location.pathname === '/search';
    if (path === '/profile') return location.pathname === '/profile';
    return location.pathname === path;
  };

  const navItems = [
    { path: '/insights', icon: FaLightbulb, label: t('app.nav.insights') },
    { path: '/my-lists', icon: FaClipboardList, label: t('app.nav.myLists') },
    { path: '/trends', icon: FaChartLine, label: t('app.nav.trends') },
    { path: '/search', icon: FaSearch, label: t('app.nav.search') },
    { path: '/tracking', icon: FaBinoculars, label: t('app.nav.tracking') },
    { path: '/chatbot', icon: FaRobot, label: t('app.nav.chatbot', 'Chatbot'), badge: 'Beta' },
    // { path: '/reports', icon: FaFileAlt, label: t('app.nav.reports', 'דו"חות') }, // hidden for now
    { path: '/signals', icon: FaBolt, label: t('app.nav.signalFeed', 'Signal Feed') },
    { path: '/profile', icon: FaUserCircle, label: t('app.nav.profile') },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: FaShieldAlt, label: t('app.nav.admin') });
  }

  const sidebarBg = isDark ? '#0c0c0f' : '#ffffff';
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const textColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.6)';
  const textActive = isDark ? '#ffffff' : '#0F172A';
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const activeBg = isDark ? 'rgba(10,132,255,0.12)' : 'rgba(0,102,204,0.08)';

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .nav-sidebar {
          position: fixed;
          top: 0; ${isRTL ? 'right: 0;' : 'left: 0;'} bottom: 0;
          width: 64px;
          background: ${sidebarBg};
          border-${isRTL ? 'left' : 'right'}: 1px solid ${borderColor};
          display: flex;
          flex-direction: column;
          z-index: 200;
          transition: width 0.22s cubic-bezier(0.4,0,0.2,1);
          overflow: hidden;
          direction: ${isRTL ? 'rtl' : 'ltr'};
        }
        .nav-sidebar.expanded {
          width: 220px;
        }
        .nav-label {
          opacity: 0;
          width: 0;
          overflow: hidden;
          white-space: nowrap;
          transition: opacity 0.18s ease, width 0.22s cubic-bezier(0.4,0,0.2,1);
          font-size: 14px;
          font-weight: 500;
          letter-spacing: -0.01em;
        }
        .nav-sidebar.expanded .nav-label {
          opacity: 1;
          width: auto;
          margin-inline-start: 10px;
        }
        .nav-item-row {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          height: 40px;
          border-radius: 10px;
          margin: 2px 8px;
          cursor: pointer;
          position: relative;
          transition: background 0.15s ease;
          text-decoration: none;
          flex-shrink: 0;
        }
        .nav-sidebar.expanded .nav-item-row {
          justify-content: flex-start;
          padding: 0 12px;
        }
        .nav-item-row:hover {
          background: ${hoverBg};
        }
        .nav-item-row.active {
          background: ${activeBg};
        }
        .nav-icon-circle {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.15s ease;
        }
        .nav-logo-area {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px 0 12px;
          flex-shrink: 0;
          cursor: pointer;
          direction: ltr;
        }
        .nav-sidebar.expanded .nav-logo-area {
          justify-content: flex-start;
          padding: 16px 14px 12px;
        }
        .nav-logo-text {
          opacity: 0;
          width: 0;
          overflow: hidden;
          white-space: nowrap;
          transition: opacity 0.18s ease, width 0.22s cubic-bezier(0.4,0,0.2,1);
          display: flex;
          align-items: center;
          margin-inline-start: 10px;
          direction: ltr;
        }
        .nav-sidebar.expanded .nav-logo-text {
          opacity: 1;
          width: auto;
        }
        .nav-bottom {
          margin-top: auto;
          padding: 8px 0 12px;
          border-top: 1px solid ${borderColor};
          flex-shrink: 0;
        }
        .nav-switcher-wrap {
          padding: 8px 8px 4px;
          overflow: visible;
        }
        .nav-sidebar:not(.expanded) .nav-switcher-wrap {
          display: flex;
          justify-content: center;
          padding: 8px 0 4px;
        }
        .nav-divider {
          height: 1px;
          background: ${borderColor};
          margin: 6px 8px;
        }
        .nav-lang-btn {
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          letter-spacing: 0.02em;
        }
      ` }} />
      <div
        className={`nav-sidebar${expanded ? ' expanded' : ''}`}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        {/* Logo */}
        <div className="nav-logo-area" onClick={() => navigate('/')}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'rgba(10,132,255,0.15)',
            border: '1px solid rgba(10,132,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, overflow: 'hidden',
          }}>
            <Image
              source={require('../assets/images/ReactFirstR.png')}
              style={{ width: 24, height: 24, objectFit: 'contain' }}
            />
          </div>
          <div className="nav-logo-text">
            <span style={{ color: textActive, fontSize: 16, fontWeight: '700', letterSpacing: -0.3 }}>ReactFirst</span>
            <span style={{ color: '#0a84ff', fontSize: 16, fontWeight: '800', marginLeft: 2 }}>AI</span>
          </div>
        </div>

        <div className="nav-divider" />

        {/* Nav items */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingTop: 6 }}>
          {navItems.map((item) => {
            const active = isActive(item.path);
            const IconComp = item.icon;
            return (
              <div
                key={item.path}
                className={`nav-item-row${active ? ' active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <div className="nav-icon-circle" style={{
                  background: active ? 'rgba(10,132,255,0.2)' : 'transparent',
                }}>
                  <IconComp size={16} color={active ? '#0a84ff' : textColor} />
                </div>
                <span className="nav-label" style={{ color: active ? textActive : textColor, fontWeight: active ? 600 : 500 }}>
                  {item.label}
                  {item.badge && (
                    <span style={{
                      marginInlineStart: 6,
                      fontSize: 11,
                      fontWeight: 500,
                      color: '#0288d1',
                      background: '#e0f7fa',
                      borderRadius: 4,
                      padding: '2px 8px',
                      verticalAlign: 'middle',
                    }}>{item.badge}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        <div className="nav-bottom">
          <div
            className="nav-item-row"
            onClick={() => i18n.changeLanguage(isRTL ? 'en' : 'he')}
            title={t('app.nav.switchLang')}
          >
            <div className="nav-icon-circle">
              <span className="nav-lang-btn">{isRTL ? 'עב' : 'EN'}</span>
            </div>
            <span className="nav-label" style={{ color: textColor }}>
              {t('app.nav.switchLang')}
            </span>
          </div>

          {/* Theme toggle */}
          <div
            className="nav-item-row"
            onClick={toggleTheme}
            title={isDark ? t('app.nav.darkMode') : t('app.nav.lightMode')}
          >
            <div className="nav-icon-circle">
              {isDark
                ? <FaMoon size={15} color={textColor} />
                : <FaSun size={15} color={textColor} />}
            </div>
            <span className="nav-label" style={{ color: textColor }}>
              {isDark ? t('app.nav.darkMode') : t('app.nav.lightMode')}
            </span>
          </div>

          {/* Org Switcher */}
          <div className="nav-switcher-wrap" style={{ zIndex: 9999 }}>
            <OrgSwitcher />
          </div>
        </div>
      </div>
    </>
  );
};

export default WebNavigation;
